import React, { useState, useEffect, useCallback } from "react";
import { X, Download, Pencil, ShieldCheck, Building2, HeartHandshake, Activity, CheckCircle2, Ban, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ccInput, ccLabel, Stars } from "./care/ui";
import { downloadProfilePdf } from "./lib/profilePdf";
import { saveOverride, CG_OVERRIDE_KEY, FAC_OVERRIDE_KEY } from "./lib/overrides";
import ContactButtons from "./lib/ContactButtons";
import SocialLinks, { SOCIAL_PLATFORMS } from "./lib/SocialLinks";

const API = process.env.REACT_APP_BACKEND_URL;

const labelCls = "block text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-1";
const fmtTs = (ts) => (ts ? new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

const Field = ({ k, v }) => (
  <div className="min-w-0">
    <p className="text-[9px] font-extrabold uppercase tracking-wider text-[#9CA3AF]">{k}</p>
    <p className="text-xs font-bold text-[#374151] break-words">{v || "—"}</p>
  </div>
);

// Inline edit form for a daycare application (master override)
const ApplicationEditor = ({ app, onSaved }) => {
  const [form, setForm] = useState({
    facilityName: app.facilityName,
    ownerName: app.ownerName,
    email: app.email,
    phone: app.phone || "",
    licenseNumber: app.licenseNumber || "",
    jurisdiction: app.jurisdiction || "",
    taxId: app.taxId || "",
    maxCapacity: app.maxCapacity || 0,
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/master/onboarding/${app.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...form, maxCapacity: parseInt(form.maxCapacity, 10) || 0 }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Save failed");
      toast.success("Application updated.");
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="grid grid-cols-2 gap-3 mt-3 p-3 bg-white border border-[#B3E0F2] rounded-xl" data-testid={`app-edit-form-${app.id}`}>
      {[
        ["facilityName", "Facility Name"], ["ownerName", "Owner Name"], ["email", "Email"], ["phone", "Phone"],
        ["licenseNumber", "License #"], ["jurisdiction", "Jurisdiction"], ["taxId", "Tax ID"],
      ].map(([key, label]) => (
        <div key={key}>
          <label className={labelCls}>{label}</label>
          <input type={key === "email" ? "email" : "text"} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className={ccInput} data-testid={`app-edit-${key}-${app.id}`} />
        </div>
      ))}
      <div>
        <label className={labelCls}>Max Capacity</label>
        <input type="number" min="0" value={form.maxCapacity} onChange={(e) => setForm({ ...form, maxCapacity: e.target.value })} className={ccInput} data-testid={`app-edit-capacity-${app.id}`} />
      </div>
      <div className="col-span-2">
        <button type="submit" disabled={saving} className="px-4 py-2 bg-[#E2F4E1] hover:bg-[#C4E5C2] border border-[#C4E5C2] text-[#2F6031] rounded-full text-xs font-bold disabled:opacity-50" data-testid={`app-edit-save-${app.id}`}>
          {saving ? "Saving..." : "Save Override"}
        </button>
      </div>
    </form>
  );
};

// Inline edit form for an activity log entry
const ActivityEditor = ({ activity, onSaved }) => {
  const [form, setForm] = useState({ action: activity.action, details: activity.details, user_email: activity.user_email || "" });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/master/activity/${activity.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Save failed");
      toast.success("Log entry updated.");
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-2 mt-2" data-testid={`activity-edit-form-${activity.id}`}>
      <div className="grid grid-cols-2 gap-2">
        <input value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} className={ccInput} placeholder="ACTION" data-testid={`activity-edit-action-${activity.id}`} />
        <input value={form.user_email} onChange={(e) => setForm({ ...form, user_email: e.target.value })} className={ccInput} placeholder="user email" data-testid={`activity-edit-email-${activity.id}`} />
      </div>
      <textarea value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} className={`${ccInput} min-h-[60px]`} placeholder="Details" data-testid={`activity-edit-details-${activity.id}`} />
      <button type="submit" disabled={saving} className="px-3.5 py-1.5 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] rounded-full text-[11px] font-bold disabled:opacity-50" data-testid={`activity-edit-save-${activity.id}`}>
        {saving ? "Saving..." : "Save Log"}
      </button>
    </form>
  );
};

// Employee (NestTrack staff) editor
const EmployeeEditor = ({ employee, onSaved }) => {
  const [form, setForm] = useState({ name: employee.name, position: employee.position });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/master/staff/${employee.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Save failed");
      toast.success("Employee record updated.");
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="flex gap-2 mt-2 items-end flex-wrap" data-testid="employee-edit-form">
      <div className="flex-1 min-w-[140px]">
        <label className={labelCls}>Name</label>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={ccInput} data-testid="employee-edit-name" />
      </div>
      <div>
        <label className={labelCls}>Position</label>
        <select value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className={ccInput} data-testid="employee-edit-position">
          {["admin", "secretary", "cfo", "accountant"].map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </div>
      <button type="submit" disabled={saving} className="px-4 py-2.5 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] rounded-full text-xs font-bold disabled:opacity-50" data-testid="employee-edit-save">
        {saving ? "Saving..." : "Save"}
      </button>
    </form>
  );
};

// ---------- Account (backend-real) review ----------
const AccountReview = ({ email, onChanged, onClose, currentUserEmail }) => {
  const [bundle, setBundle] = useState(null);
  const [editingApp, setEditingApp] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const staffAction = async (userId, action) => {
    try {
      const url = action === "delete" ? `${API}/api/master/staff/${userId}` : `${API}/api/master/staff/${userId}/${action}`;
      const res = await fetch(url, { method: action === "delete" ? "DELETE" : "POST", credentials: "include" });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Action failed");
      toast.success(action === "delete" ? "Account permanently deleted." : `Account ${action === "suspend" ? "suspended" : "reactivated"}.`);
      setConfirmDelete(null);
      if (action === "delete") onClose?.();
      else refresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const appAction = async (appId, action) => {
    try {
      const url = action === "delete" ? `${API}/api/master/onboarding/${appId}` : `${API}/api/master/onboarding/${appId}/${action}`;
      const res = await fetch(url, { method: action === "delete" ? "DELETE" : "POST", credentials: "include" });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Action failed");
      toast.success(action === "delete" ? "Daycare record permanently deleted." : `Daycare ${action === "suspend" ? "suspended" : "reactivated"}.`);
      setConfirmDelete(null);
      window.dispatchEvent(new Event("nt:onboarding-changed"));
      if (action === "delete") onClose?.();
      else refresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/master/account/${encodeURIComponent(email)}`, { credentials: "include" });
      if (!res.ok) throw new Error();
      setBundle(await res.json());
    } catch {
      toast.error("Could not load account record.");
    }
  }, [email]);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = () => {
    setEditingApp(null);
    setEditingActivity(null);
    setEditingEmployee(false);
    load();
    onChanged?.();
    window.dispatchEvent(new Event("nt:onboarding-changed"));
  };

  if (!bundle) return <p className="text-xs text-[#9CA3AF] py-8 text-center font-medium">Loading full record…</p>;

  const { employee, applications, facility, activities } = bundle;
  const primary = applications[0];

  const pdf = () =>
    downloadProfilePdf({
      title: employee ? employee.name : primary?.facilityName || email,
      subtitle: `Account: ${email}${employee ? ` · NestTrack ${employee.position}` : ""}${primary ? ` · Application ${primary.status}` : ""}`,
      fields: [
        ["Email", email],
        ...(employee ? [["Employee Name", employee.name], ["Position", employee.position], ["Joined", fmtTs(employee.created_at)]] : []),
        ...(primary
          ? [["Facility", primary.facilityName], ["Owner", primary.ownerName], ["Phone", primary.phone], ["License #", primary.licenseNumber], ["Jurisdiction", primary.jurisdiction], ["Tax ID", primary.taxId], ["Capacity", String(primary.maxCapacity || "—")], ["Status", primary.status]]
          : []),
        ...(facility ? [["Facility Activated", facility.activated ? "Yes" : "No"], ["Approved At", fmtTs(facility.approved_at)]] : []),
      ],
      activities,
    });

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1.5 flex-wrap">
          {employee && <span className="px-2.5 py-1 bg-[#374151] text-white text-[9px] font-extrabold uppercase rounded-full" data-testid="review-badge-employee">NestTrack {employee.position}</span>}
          {primary && <span className="px-2.5 py-1 bg-[#E6F2FA] border border-[#B3E0F2] text-[#3D7E9E] text-[9px] font-extrabold uppercase rounded-full" data-testid="review-badge-applicant">Daycare · {primary.status}</span>}
          {!employee && !primary && <span className="px-2.5 py-1 bg-[#FFF2D1] text-[#A08244] text-[9px] font-extrabold uppercase rounded-full">Activity only</span>}
        </div>
        <button onClick={pdf} data-testid="record-pdf-btn" className="flex items-center gap-1.5 px-4 py-2 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-xs font-bold transition-all shadow-sm shadow-pink-200">
          <Download className="w-3.5 h-3.5" /> Download PDF
        </button>
        {(employee || primary) && (
          <button
            onClick={() => {
              navigator.clipboard.writeText(`${API}/api/share/profile/${employee?.id || primary?.id}`);
              toast.success("Share link copied — rich preview card included.");
            }}
            data-testid="record-share-btn"
            className="flex items-center gap-1.5 px-4 py-2 bg-white border border-[#B3E0F2] text-[#3D7E9E] rounded-full text-xs font-bold hover:bg-[#E6F2FA] transition-colors"
          >
            Copy Share Link
          </button>
        )}
      </div>

      <ContactButtons phone={primary?.phone} email={email} testId="record-contact" />

      {employee && (
        <div className="p-4 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl" data-testid="review-employee">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-extrabold font-heading flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-[#5CB3DC]" /> NestTrack Employee</h4>
            <button onClick={() => setEditingEmployee((v) => !v)} data-testid="employee-edit-toggle" className="flex items-center gap-1 text-[10px] font-bold text-[#5CB3DC] hover:underline">
              <Pencil className="w-3 h-3" /> Edit
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Field k="Name" v={employee.name} />
            <Field k="Position" v={employee.position} />
            <Field k="Status" v={employee.status || "active"} />
            <Field k="Joined" v={fmtTs(employee.created_at)} />
          </div>
          {employee.position !== "owner" && employee.email !== currentUserEmail && (
            <div className="flex gap-2 mt-3 flex-wrap" data-testid="employee-moderation">
            {employee.status === "suspended" ? (
              <button onClick={() => staffAction(employee.id, "reactivate")} data-testid="employee-reactivate-btn" className="flex items-center gap-1.5 px-3.5 py-2 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] rounded-full text-[11px] font-bold">
                <RotateCcw className="w-3 h-3" /> Reactivate
              </button>
            ) : (
              <button onClick={() => staffAction(employee.id, "suspend")} data-testid="employee-suspend-btn" className="flex items-center gap-1.5 px-3.5 py-2 bg-[#FFF2D1] border border-[#F5E3B3] text-[#A08244] rounded-full text-[11px] font-bold">
                <Ban className="w-3 h-3" /> Suspend
              </button>
            )}
            {confirmDelete === `emp-${employee.id}` ? (
              <button onClick={() => staffAction(employee.id, "delete")} data-testid="employee-delete-confirm" className="px-3.5 py-2 bg-rose-500 text-white rounded-full text-[11px] font-bold">
                Confirm permanent delete?
              </button>
            ) : (
              <button onClick={() => setConfirmDelete(`emp-${employee.id}`)} data-testid="employee-delete-btn" className="flex items-center gap-1.5 px-3.5 py-2 bg-rose-50 border border-rose-200 text-rose-500 rounded-full text-[11px] font-bold">
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            )}
          </div>
          )}
          {editingEmployee && <EmployeeEditor employee={employee} onSaved={refresh} />}
        </div>
      )}

      {applications.map((a) => (
        <div key={a.id} className="p-4 bg-[#FFF9F2] border border-[#E6F2FA] rounded-2xl" data-testid={`review-application-${a.id}`}>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-extrabold font-heading flex items-center gap-2"><Building2 className="w-3.5 h-3.5 text-[#5CB3DC]" /> Daycare Application · {a.status}</h4>
            <button onClick={() => setEditingApp(editingApp === a.id ? null : a.id)} data-testid={`app-edit-toggle-${a.id}`} className="flex items-center gap-1 text-[10px] font-bold text-[#5CB3DC] hover:underline">
              <Pencil className="w-3 h-3" /> Edit
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Field k="Facility" v={a.facilityName} />
            <Field k="Owner" v={a.ownerName} />
            <Field k="Phone" v={a.phone} />
            <Field k="License #" v={a.licenseNumber} />
            <Field k="Jurisdiction" v={a.jurisdiction} />
            <Field k="Tax ID" v={a.taxId} />
            <Field k="Capacity" v={String(a.maxCapacity || "—")} />
            <Field k="Applied" v={fmtTs(a.createdAt)} />
            <Field k="Reviewed" v={fmtTs(a.reviewedAt)} />
            <Field k="Address" v={a.address} />
            <Field k="ID Document" v={a.idDocumentName} />
            <Field k="License Docs" v={a.licenseDocuments?.map((d) => `${d.category} (${d.fileName})`).join(", ") || "—"} />
          </div>
          <div className="flex gap-2 mt-3 flex-wrap" data-testid={`app-moderation-${a.id}`}>
            {["approved", "activated"].includes(a.status) && (
              <button onClick={() => appAction(a.id, "suspend")} data-testid={`app-suspend-${a.id}`} className="flex items-center gap-1.5 px-3.5 py-2 bg-[#FFF2D1] border border-[#F5E3B3] text-[#A08244] rounded-full text-[11px] font-bold">
                <Ban className="w-3 h-3" /> Suspend
              </button>
            )}
            {a.status === "suspended" && (
              <button onClick={() => appAction(a.id, "reactivate")} data-testid={`app-reactivate-${a.id}`} className="flex items-center gap-1.5 px-3.5 py-2 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] rounded-full text-[11px] font-bold">
                <RotateCcw className="w-3 h-3" /> Reactivate
              </button>
            )}
            {confirmDelete === `app-${a.id}` ? (
              <button onClick={() => appAction(a.id, "delete")} data-testid={`app-delete-confirm-${a.id}`} className="px-3.5 py-2 bg-rose-500 text-white rounded-full text-[11px] font-bold">
                Confirm permanent delete?
              </button>
            ) : (
              <button onClick={() => setConfirmDelete(`app-${a.id}`)} data-testid={`app-delete-${a.id}`} className="flex items-center gap-1.5 px-3.5 py-2 bg-rose-50 border border-rose-200 text-rose-500 rounded-full text-[11px] font-bold">
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            )}
          </div>
          {editingApp === a.id && <ApplicationEditor app={a} onSaved={refresh} />}
        </div>
      ))}

      <div data-testid="review-activities">
        <h4 className="text-xs font-extrabold font-heading flex items-center gap-2 mb-2"><Activity className="w-3.5 h-3.5 text-[#D9A93E]" /> Associated Activity ({activities.length})</h4>
        {activities.length === 0 ? (
          <p className="text-[11px] text-[#9CA3AF] font-medium">No activity logged for this account yet.</p>
        ) : (
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {activities.map((act) => (
              <div key={act.id} className="p-3 bg-white border border-[#FCE6E9] rounded-xl" data-testid={`review-activity-${act.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="px-2 py-0.5 bg-[#E6F2FA] text-[#3D7E9E] text-[9px] font-extrabold uppercase rounded-full">{act.action}</span>
                  <div className="flex items-center gap-2">
                    {act.edited && <span className="text-[9px] font-bold text-[#D9A93E]">edited</span>}
                    <span className="text-[10px] text-[#9CA3AF]">{fmtTs(act.ts)}</span>
                    <button onClick={() => setEditingActivity(editingActivity === act.id ? null : act.id)} data-testid={`activity-edit-toggle-${act.id}`} className="text-[#9CA3AF] hover:text-[#5CB3DC] transition-colors">
                      <Pencil className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-[#6B7280] mt-1">{act.details}</p>
                {editingActivity === act.id && <ActivityEditor activity={act} onSaved={refresh} />}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Caregiver (directory) review ----------
const CaregiverReview = ({ caregiver, onChanged }) => {
  const [c, setC] = useState(caregiver);
  const [editing, setEditing] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [form, setForm] = useState({ name: caregiver.name, title: caregiver.title, bio: caregiver.bio, hourlyRate: caregiver.hourlyRate, rateMax: caregiver.rateMax, city: caregiver.city, status: caregiver.status, phone: caregiver.phone || "", email: caregiver.email || "", socials: caregiver.socials || {} });

  const save = (e) => {
    e.preventDefault();
    const patch = { ...form, hourlyRate: parseInt(form.hourlyRate, 10) || 0, rateMax: parseInt(form.rateMax, 10) || 0 };
    const merged = saveOverride(CG_OVERRIDE_KEY, c.id, patch);
    setC((prev) => ({ ...prev, ...merged }));
    toast.success("Caregiver override saved.");
    setEditing(false);
    onChanged?.();
  };

  const moderate = (patch, msg) => {
    const merged = saveOverride(CG_OVERRIDE_KEY, c.id, patch);
    setC((prev) => ({ ...prev, ...merged }));
    toast.success(msg);
    onChanged?.();
  };
  const pdf = () =>
    downloadProfilePdf({
      title: c.name,
      subtitle: `Caregiver · ${c.title} · ${c.status}`,
      fields: [
        ["Title", c.title], ["Status", c.status], ["City", c.city], ["ZIP", c.zip], ["Rate", `$${c.hourlyRate}–$${c.rateMax}/hr`],
        ["Experience", `${c.yearsExperience} yrs`], ["Rating", `${c.rating} (${c.reviewCount} reviews)`],
        ["Services", c.services], ["Categories", c.categories],
        ["Credentials", c.credentials.map((cr) => `${cr.type}${cr.verified ? " ✓" : ""}`)],
        ["Availability", Object.entries(c.availability || {}).map(([d, h]) => `${d}: ${h}`)],
        ["Bio", c.bio],
      ],
    });

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3.5">
          <img src={c.photo} alt={c.name} className="w-16 h-16 rounded-full object-cover border-2 border-[#FCE6E9]" />
          <div>
            <h4 className="text-base font-black font-heading text-[#374151]">{c.name}</h4>
            <p className="text-xs text-[#9CA3AF] font-medium">{c.title}</p>
            <Stars rating={c.rating} />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <button onClick={pdf} data-testid="record-pdf-btn" className="flex items-center gap-1.5 px-4 py-2 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-xs font-bold shadow-sm shadow-pink-200">
            <Download className="w-3.5 h-3.5" /> PDF
          </button>
          <button onClick={() => setEditing((v) => !v)} data-testid="caregiver-edit-toggle" className="flex items-center gap-1.5 px-4 py-2 bg-white border border-[#B3E0F2] text-[#3D7E9E] rounded-full text-xs font-bold">
            <Pencil className="w-3 h-3" /> Edit
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Field k="Status" v={c.status} />
        <Field k="City" v={c.city} />
        <Field k="Rate" v={`$${c.hourlyRate}–$${c.rateMax}/hr`} />
        <Field k="Experience" v={`${c.yearsExperience} yrs`} />
        <Field k="Reviews" v={`${c.rating} · ${c.reviewCount}`} />
        <Field k="Radius" v={`${c.radiusMiles} mi`} />
      </div>
      <div>
        <p className="text-[9px] font-extrabold uppercase tracking-wider text-[#9CA3AF] mb-1.5">Verified Credentials</p>
        <div className="flex gap-1.5 flex-wrap">
          {c.credentials.map((cr) => (
            <span key={cr.type} className={`px-2 py-0.5 text-[10px] font-bold rounded-full border ${cr.verified ? "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]" : "bg-[#FFF2D1] border-[#F5E3B3] text-[#A08244]"}`}>
              {cr.type} {cr.verified && "✓"}
            </span>
          ))}
        </div>
      </div>
      <p className="text-xs text-[#6B7280] leading-relaxed">{c.bio}</p>
      <ContactButtons phone={c.phone} email={c.email} testId="cg-contact" />
      <SocialLinks links={c.socials || {}} testId="cg-social" />
      <div className="flex gap-2 flex-wrap pt-1" data-testid="cg-moderation">
        {c.status === "suspended" ? (
          <button onClick={() => moderate({ status: "active" }, "Caregiver reactivated.")} data-testid="cg-reactivate-btn" className="flex items-center gap-1.5 px-3.5 py-2 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] rounded-full text-[11px] font-bold">
            <RotateCcw className="w-3 h-3" /> Reactivate
          </button>
        ) : (
          <button onClick={() => moderate({ status: "suspended" }, "Caregiver suspended.")} data-testid="cg-suspend-btn" className="flex items-center gap-1.5 px-3.5 py-2 bg-[#FFF2D1] border border-[#F5E3B3] text-[#A08244] rounded-full text-[11px] font-bold">
            <Ban className="w-3 h-3" /> Suspend
          </button>
        )}
        {confirmRemove ? (
          <button onClick={() => moderate({ removed: true }, "Caregiver permanently removed.")} data-testid="cg-remove-confirm" className="px-3.5 py-2 bg-rose-500 text-white rounded-full text-[11px] font-bold">
            Confirm permanent remove?
          </button>
        ) : (
          <button onClick={() => setConfirmRemove(true)} data-testid="cg-remove-btn" className="flex items-center gap-1.5 px-3.5 py-2 bg-rose-50 border border-rose-200 text-rose-500 rounded-full text-[11px] font-bold">
            <Trash2 className="w-3 h-3" /> Remove
          </button>
        )}
      </div>
      {editing && (
        <form onSubmit={save} className="grid grid-cols-2 gap-3 p-3 bg-white border border-[#B3E0F2] rounded-xl" data-testid="caregiver-edit-form">
          <div><label className={labelCls}>Name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={ccInput} data-testid="cg-edit-name" /></div>
          <div><label className={labelCls}>Title</label><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={ccInput} data-testid="cg-edit-title" /></div>
          <div><label className={labelCls}>City</label><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={ccInput} data-testid="cg-edit-city" /></div>
          <div>
            <label className={labelCls}>Status</label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={ccInput} data-testid="cg-edit-status">
              {["active", "pending", "suspended"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>Rate Min $</label><input type="number" value={form.hourlyRate} onChange={(e) => setForm({ ...form, hourlyRate: e.target.value })} className={ccInput} data-testid="cg-edit-rate" /></div>
          <div><label className={labelCls}>Rate Max $</label><input type="number" value={form.rateMax} onChange={(e) => setForm({ ...form, rateMax: e.target.value })} className={ccInput} data-testid="cg-edit-ratemax" /></div>
          <div><label className={labelCls}>Phone</label><input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={ccInput} data-testid="cg-edit-phone" /></div>
          <div><label className={labelCls}>Email</label><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={ccInput} data-testid="cg-edit-email" /></div>
          {SOCIAL_PLATFORMS.map((s) => (
            <div key={s.key}><label className={labelCls}>{s.label}</label><input type="url" value={form.socials[s.key] || ""} onChange={(e) => setForm({ ...form, socials: { ...form.socials, [s.key]: e.target.value } })} className={ccInput} placeholder={s.placeholder} data-testid={`cg-edit-social-${s.key}`} /></div>
          ))}
          <div className="col-span-2"><label className={labelCls}>Bio</label><textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className={`${ccInput} min-h-[60px]`} data-testid="cg-edit-bio" /></div>
          <div className="col-span-2">
            <button type="submit" className="px-4 py-2 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] rounded-full text-xs font-bold" data-testid="cg-edit-save">Save Override</button>
          </div>
        </form>
      )}
    </div>
  );
};

// ---------- Geo facility (directory) review ----------
const FacilityReview = ({ facility, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: facility.name, capacity: facility.capacity });

  const save = (e) => {
    e.preventDefault();
    saveOverride(FAC_OVERRIDE_KEY, facility.id, { name: form.name, capacity: parseInt(form.capacity, 10) || 0 });
    toast.success("Facility override saved.");
    setEditing(false);
    onChanged?.();
  };

  const pdf = () =>
    downloadProfilePdf({
      title: facility.name,
      subtitle: "Daycare Facility · Directory Record",
      fields: [["Name", facility.name], ["Capacity", `${facility.capacity} children`], ["Coordinates", `${facility.lat}, ${facility.lng}`], ["Subscription", "$30/month"]],
    });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 rounded-full bg-[#E6F2FA] flex items-center justify-center"><Building2 className="w-5 h-5 text-[#5CB3DC]" /></span>
          <div>
            <h4 className="text-base font-black font-heading text-[#374151]">{facility.name}</h4>
            <p className="text-xs text-[#9CA3AF] font-medium">Capacity {facility.capacity} · {facility.lat}, {facility.lng}</p>
          </div>
        </div>
        <div className="flex gap-1.5">
          <button onClick={pdf} data-testid="record-pdf-btn" className="flex items-center gap-1.5 px-4 py-2 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-xs font-bold shadow-sm shadow-pink-200">
            <Download className="w-3.5 h-3.5" /> PDF
          </button>
          <button onClick={() => setEditing((v) => !v)} data-testid="facility-edit-toggle" className="flex items-center gap-1.5 px-4 py-2 bg-white border border-[#B3E0F2] text-[#3D7E9E] rounded-full text-xs font-bold">
            <Pencil className="w-3 h-3" /> Edit
          </button>
        </div>
      </div>
      {editing && (
        <form onSubmit={save} className="flex gap-2 items-end flex-wrap p-3 bg-white border border-[#B3E0F2] rounded-xl" data-testid="facility-edit-form">
          <div className="flex-1 min-w-[160px]"><label className={labelCls}>Name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={ccInput} data-testid="fac-edit-name" /></div>
          <div><label className={labelCls}>Capacity</label><input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className={ccInput} data-testid="fac-edit-capacity" /></div>
          <button type="submit" className="px-4 py-2.5 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] rounded-full text-xs font-bold" data-testid="fac-edit-save">Save Override</button>
        </form>
      )}
    </div>
  );
};

// ---------- Shell ----------
export default function MasterRecordReview({ review, onClose, onChanged, currentUserEmail }) {
  useEffect(() => {
    if (!review) return;
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [review, onClose]);
  if (!review) return null;
  const titles = { account: review.email, caregiver: review.caregiver?.name, facility: review.facility?.name };
  return (
    <div className="fixed inset-0 z-50 bg-[#374151]/30 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in" data-testid="record-review-modal" onClick={onClose}>
      <div className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-2xl w-full p-7 shadow-xl animate-modal-in max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-5 border-b border-[#FCE6E9] pb-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="w-9 h-9 rounded-full bg-[#FCE6E9] flex items-center justify-center shrink-0">
              {review.type === "caregiver" ? <HeartHandshake className="w-4 h-4 text-[#E5A0BC]" /> : review.type === "facility" ? <Building2 className="w-4 h-4 text-[#5CB3DC]" /> : <CheckCircle2 className="w-4 h-4 text-[#E5A0BC]" />}
            </span>
            <div className="min-w-0">
              <p className="text-[9px] font-extrabold uppercase tracking-wider text-[#9CA3AF]">Full Record Review</p>
              <h3 className="text-base font-extrabold font-heading text-[#374151] truncate" data-testid="record-review-title">{titles[review.type]}</h3>
            </div>
          </div>
          <button onClick={onClose} data-testid="record-review-close" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] hover:text-[#374151] transition-colors shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
        {review.type === "account" && <AccountReview email={review.email} onChanged={onChanged} onClose={onClose} currentUserEmail={currentUserEmail} />}
        {review.type === "caregiver" && <CaregiverReview caregiver={review.caregiver} onChanged={onChanged} />}
        {review.type === "facility" && <FacilityReview facility={review.facility} onChanged={onChanged} />}
      </div>
    </div>
  );
}
